// src/pages/fleet/TripListPage.tsx — list of trips.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getTrips } from '../../services/fleetApi';
import { P } from './fleetStyles';
import { tripBadge } from './tripBadge';
import type { Trip, TripStatus } from '../../types';

const STATUS_COLOR: Record<TripStatus, string> = { PENDING: '#E8941A', ACTIVE: '#22A559', COMPLETED: '#6B7280', CANCELLED: '#DC3545' };
const STATUS_LABEL: Record<TripStatus, string> = { PENDING: 'AT PLANT', ACTIVE: 'ON GOING', COMPLETED: 'COMPLETED', CANCELLED: 'CANCELLED' };

// How long a trip has been waiting at the plant, or how long the journey has run.
// A trip only becomes ACTIVE when the truck actually leaves the pickup point.
function sinceText(t: Trip): { text: string; warn: boolean } {
  const from = t.status === 'PENDING' ? t.createdAt : t.startedAt;
  if (!from) return { text: '—', warn: false };
  const min = Math.max(0, Math.round((Date.now() - new Date(from).getTime()) / 60000));
  const h = Math.floor(min / 60);
  return { text: h ? `${h}h ${min % 60}m` : `${min}m`, warn: t.status === 'PENDING' && min >= 240 };
}

/** One word about IFMS, next to the trip's own status. */
function IfmsChip({ state }: { state: 'pending' | 'attention' }) {
  const look = {
    pending:   { text: 'IFMS pending',  fg: 'var(--fx-text-2)', bg: 'var(--fx-gray-soft)',   title: 'On its way to IFMS — queued, retrying, or waiting for the call before it. Nothing to do.' },
    attention: { text: 'IFMS stuck',    fg: 'var(--fx-orange)', bg: 'var(--fx-orange-soft)', title: 'Waiting on a person: data to fix, or a report whose outcome needs confirming.' },
  }[state];
  return (
    <span title={look.title} style={{
      marginLeft: 6, fontSize: 10, fontWeight: 700, color: look.fg, background: look.bg,
      borderRadius: 20, padding: '2px 7px', whiteSpace: 'nowrap',
    }}>{look.text}</span>
  );
}

export default function TripListPage() {
  const [trips, setTrips] = useState<Trip[]>([]);
  // Status lives in the URL, so dashboard cards can link straight to a filtered list.
  const [params, setParams] = useSearchParams();
  const filter = params.get('status') || '';
  const setFilter = (s: string) => setParams(s ? { status: s } : {}, { replace: true });
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const [err, setErr] = useState<string>('');
  const seq = useRef(0);   // newest request wins when the filter is switched quickly

  const load = () => {
    const mine = ++seq.current;
    setLoading(true); setErr('');
    getTrips(filter ? { status: filter } : {})
      .then(r => { if (mine === seq.current) setTrips(r.data.trips || []); })
      .catch(() => { if (mine === seq.current) { setTrips([]); setErr('Could not load trips. Please try again.'); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  };
  useEffect(load, [filter]);

  return (
    <div style={P.page}>
      <div style={P.head}>
        <div><h1 style={P.h1}>📍 Trips</h1><p style={P.sub}>All fleet trips</p></div>
        <Link to="/fleet/trips/new" style={P.btnPrimary}>➕ New Trip</Link>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['', 'ACTIVE', 'PENDING', 'COMPLETED', 'CANCELLED'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ ...P.chip, ...(filter === s ? P.chipOn : {}) }}>{s || 'All'}</button>
        ))}
      </div>

      <div style={{ ...P.card, overflowX: 'auto' }}>
        <table style={P.table}>
          <thead><tr>{['Trip #', 'Vehicle', 'Route', 'Status', 'Waiting / Running', 'Pings'].map(h => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
          <tbody>
            {trips.map(t => (
              <tr key={t.id} style={P.trClick} onClick={() => navigate(`/fleet/trips/${t.id}`)}>
                <td style={P.td}><b>{t.tripNumber}</b></td>
                <td style={P.td}>{t.vehicle?.vehicleNumber}<div style={P.muted}>{t.vehicle?.driverName}</div></td>
                <td style={P.td}>{t.fromLocation} → {t.toLocation}</td>
                <td style={P.td}>
                  {(() => { const b = tripBadge(t); return (
                    <span style={{ ...P.badge, background: b.color }}>{b.label}</span>
                  ); })()}
                  {/* The badge already says whether IFMS has it. This only adds the
                      reason when there is something to say — and nothing at all
                      while reporting is off, or it would be noise on every row. */}
                  {(t.ifms === 'pending' || t.ifms === 'attention') && <IfmsChip state={t.ifms} />}
                </td>
                <td style={P.td}>
                  {(() => {
                    const s = sinceText(t);
                    return (
                      <span style={{ color: s.warn ? 'var(--fx-orange)' : 'var(--fx-text)', fontWeight: s.warn ? 600 : 400 }}>
                        {s.text}
                        <div style={P.muted}>{t.status === 'PENDING' ? 'at plant' : t.status === 'ACTIVE' ? 'on the road' : ''}</div>
                      </span>
                    );
                  })()}
                </td>
                <td style={P.td}>{t._count?.locations ?? 0}</td>
              </tr>
            ))}
            {err && <tr><td colSpan={6} style={{ ...P.emptyTd, color: 'var(--fx-red)' }}>{err}</td></tr>}
            {!loading && !err && trips.length === 0 && <tr><td colSpan={6} style={P.emptyTd}>No trips</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
